import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { GameCollectionAction, GameCollectionSelector } from "@obg/gaming.external";
import { CoreHttpClient, CoreState, Customer, MarketSelector } from "@obg/ngx.core";
import { CacheFactory, CacheStore, StorageMode } from "@obg/ngx.global";
import { flattenDeep, isEmpty, kebabCase, map as lodashMap, union, uniq } from "lodash-es";
import { Observable } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { GameCollectionConfigService } from "./game-collection.config";
import { GameCollectionRaw, GameCollectionResponse } from "./game-collection.model";

@Injectable({
	providedIn: "root",
})
export class GameCollectionService {
	private cache: CacheStore;
	private marketLanguageCode: string | undefined;

	constructor(
		private httpClient: CoreHttpClient,
		private config: GameCollectionConfigService,
		cacheFactory: CacheFactory,
		private store: Store<CoreState>,
		private selector: GameCollectionSelector,
		private actions: GameCollectionAction,
		private marketSelector: MarketSelector
	) {
		this.cache = cacheFactory.get("gaming.gameCollection", {
			storageMode: StorageMode.LocalStorage,
			isLanguageSpecific: true,
		});

		this.store.select(this.marketSelector.getLanguageCode()).pipe(
			tap((marketCode) => {
				this.marketLanguageCode = marketCode;
			})
		).subscribe;
	}

	getAll(customer: Customer | undefined): Observable<Record<string, GameCollectionRaw>> {
		const customerId = customer?.customerId;
		const countryCode = customer?.address?.country;

		return this.cache.getOrSetAsObservable(
			`all-${customerId || "anonymous"}-${countryCode || this.marketLanguageCode}`,
			() =>
				this.httpClient
					.get<GameCollectionResponse[]>({
						url: this.config.getGameCollectionEndpoint(),
						searchParams: { countryCode },
					})
					.pipe(map((collections) => this.normalizeCollections(collections))),
			{ maxAge: this.config.getCacheMaxAgeForGetAll() }
		);
	}

	getById(collectionId: string): Observable<Record<string, GameCollectionRaw>> {
		return this.httpClient
			.get<GameCollectionResponse>({
				url: this.config.getGameCollectionByIdEndpoint(),
				searchParams: { collectionId },
			})
			.pipe(map((collection) => this.normalizeCollections([collection])));
	}

	isGameCollectionExists(slug: string): Observable<boolean> {
		return this.store.pipe(
			select(this.selector.getAll()),
			take(1),
			tap((collections) => {
				if (isEmpty(collections)) {
					this.store.dispatch(this.actions.getAll());
				}
			}),
			switchMap(() =>
				this.store.pipe(
					select(this.selector.getBySlug(slug)),
					filter((collection) => collection !== undefined),
					take(1),
					map((collection) => !isEmpty(collection))
				)
			)
		);
	}

	/**
	 * Normalizes the collections from a recursive structure {Array<GameCollectionResponse>}
	 * to a flattened dictionary {Record<string, GameCollectionRaw>}. In the resulting set,
	 * each collection contains only references(ids) to its subCollections and a flattened set of
	 * games, incuding own games as well as all of its subCollections games distinct.
	 *
	 * todo: in the future we should evaluate whether this should be done by the API.
	 */
	private normalizeCollections(
		collections: GameCollectionResponse[],
		normalized: Record<string, GameCollectionRaw> = {}
	): Record<string, GameCollectionRaw> {
		if (isEmpty(collections)) {
			return normalized;
		}

		for (const collection of collections) {
			normalized = this.normalizeCollections(collection.subCollections, normalized);

			normalized[collection.id] = Object.assign({}, collection, {
				slug: isEmpty(collection.slug) ? kebabCase(collection.id) : collection.slug,
				games: this.flattenGames(collection.subCollections, collection.games),
				subCollections: lodashMap(collection.subCollections, (subCollection) => subCollection.id),
			});
		}

		return normalized;
	}

	private flattenGames(collections: GameCollectionResponse[], ids: string[] = []): string[] {
		if (isEmpty(collections)) {
			return ids;
		}

		const games = lodashMap(collections, (item) => union(this.flattenGames(item.subCollections, item.games), ids));
		const flatGames = flattenDeep(games) as string[];
		return uniq(flatGames);
	}
}
